// api.ts - Extended version with all service order endpoints

import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  communicationApiClient,
  wxApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  AssetSearchParams,
  AssignedSummaryResponse,
  AssignmentRequest,
  AssignmentResponse,
  CompletedSummaryResponse,
  ConsumerSearchParams,
  ConsumerSearchResponse,
  CreateServiceOrderParams,
  CreateServiceOrderRequest,
  CreateServiceOrderResponse,
  FieldForceRecommendParams,
  FieldForceRecommendResponse,
  HistorySummaryResponse,
  JobCardApiResponse,
  JobCardDetailParams,
  JobCardListParams,
  JobCardListParamsWithFilters,
  JobCardRecommendationParams,
  JobCardRecommendationResponse,
  JobCardReportParams,
  JobCardReportResponse,
  MeterSearchParams,
  MeterSearchResponse,
  PendingSummaryResponse,
  ServiceProcedureApiResponse,
  ServiceProcedureDetailParams,
  ServiceProcedureListParams,
  SummaryParams,
} from "./types";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TerritoryConfigResponse } from "@features/communication/lists/types";
import { ServiceAssociationResponse } from "../master/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const wxApi = {
  getJobCardReport: async (
    params: JobCardReportParams
  ): Promise<JobCardReportResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `job-card/report/${params.id}`,
      (query) => {
        query.push("field_name", params.fieldName);
        query.push("remote_utility_id", params.remoteUtilityId);
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },
  getSignatureReport: async (
    id: string,
    remoteUtilityId: string
  ): Promise<JobCardReportResponse> => {
    return wxApi.getJobCardReport({
      id,
      fieldName: "signature",
      remoteUtilityId,
    });
  },
  // Convenience methods for each report type
  getTaskReport: async (
    id: string,
    remoteUtilityId: string
  ): Promise<JobCardReportResponse> => {
    return wxApi.getJobCardReport({
      id,
      fieldName: "task",
      remoteUtilityId,
    });
  },

  getMaterialReport: async (
    id: string,
    remoteUtilityId: string
  ): Promise<JobCardReportResponse> => {
    return wxApi.getJobCardReport({
      id,
      fieldName: "material",
      remoteUtilityId,
    });
  },

  getReadingsReport: async (
    id: string,
    remoteUtilityId: string
  ): Promise<JobCardReportResponse> => {
    return wxApi.getJobCardReport({
      id,
      fieldName: "readings",
      remoteUtilityId,
    });
  },
  pendingSummary: async (
    params: SummaryParams
  ): Promise<PendingSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/pending/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
      }
    );
    const response = await wxApiClient.get(url);
    return response.data.result;
  },

  assignedSummary: async (
    params: SummaryParams
  ): Promise<AssignedSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/assigned/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
      }
    );
    const response = await wxApiClient.get(url);
    return response.data.result;
  },

  completedSummary: async (
    params: SummaryParams
  ): Promise<CompletedSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/completed/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
      }
    );
    const response = await wxApiClient.get(url);
    return response.data.result;
  },

  historySummary: async (
    params: SummaryParams
  ): Promise<HistorySummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/history/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
      }
    );
    const response = await wxApiClient.get(url);

    return response.data.result;
  },
  // Existing pending endpoint
  pending: async (
    params: JobCardListParamsWithFilters
  ): Promise<JobCardApiResponse> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_pending", "1");
        query.push("page", (filters?.page || 1).toString());
        query.push("limit", (filters?.limit || PAGE_SIZE).toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }

        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }

        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }

        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }

        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }

        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Updated assigned endpoint with filters
  assigned: async (
    params: JobCardListParamsWithFilters
  ): Promise<JobCardApiResponse> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_assigned", "1");
        query.push("page", (filters?.page || 1).toString());
        query.push("limit", (filters?.limit || PAGE_SIZE).toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }

        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }

        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }

        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }

        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }

        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Updated completed endpoint with filters
  completed: async (
    params: JobCardListParamsWithFilters
  ): Promise<JobCardApiResponse> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_completed", "1");
        query.push("page", (filters?.page || 1).toString());
        query.push("limit", (filters?.limit || PAGE_SIZE).toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }

        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }

        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }

        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }

        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }

        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }

        if (filters?.slaFilter && filters.slaFilter !== "all") {
          query.push("sla_compliance", filters.slaFilter);
        }

        if (filters?.soNameFilter) {
          query.push("so_name", filters.soNameFilter);
        }
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Updated history endpoint with filters
  history: async (
    params: JobCardListParamsWithFilters
  ): Promise<JobCardApiResponse> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_history", "1");
        query.push("page", (filters?.page || 1).toString());
        query.push("limit", (filters?.limit || PAGE_SIZE).toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }

        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }

        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }

        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }

        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }

        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }

        if (filters?.slaFilter && filters.slaFilter !== "all") {
          query.push("sla_compliance", filters.slaFilter);
        }

        if (filters?.soNameFilter) {
          query.push("so_name", filters.soNameFilter);
        }
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Add these export methods to your wxApi object

  // Export Pending Service Orders
  exportPending: async (params: JobCardListParamsWithFilters): Promise<any> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_pending", "1");
        if (filters?.page) query.push("page", filters.page.toString());
        if (filters?.limit) query.push("limit", filters.limit.toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }
        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }
        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }
        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }
        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }
        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
        query.push("export_format", "csv");
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Export Assigned Service Orders
  exportAssigned: async (
    params: JobCardListParamsWithFilters
  ): Promise<any> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_assigned", "1");
        if (filters?.page) query.push("page", filters.page.toString());
        if (filters?.limit) query.push("limit", filters.limit.toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }
        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }
        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }
        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }
        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }
        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
        query.push("export_format", "csv");
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Export Completed Service Orders
  exportCompleted: async (
    params: JobCardListParamsWithFilters
  ): Promise<any> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_completed", "1");
        if (filters?.page) query.push("page", filters.page.toString());
        if (filters?.limit) query.push("limit", filters.limit.toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }
        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }
        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }
        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }
        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }
        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
        if (filters?.slaFilter && filters.slaFilter !== "all") {
          query.push("sla_compliance", filters.slaFilter);
        }
        if (filters?.soNameFilter) {
          query.push("so_name", filters.soNameFilter);
        }
        query.push("export_format", "csv");
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },

  // Export History Service Orders
  exportHistory: async (params: JobCardListParamsWithFilters): Promise<any> => {
    const { filters, ...otherParams } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        // Basic parameters
        query.push("remote_utility_id", otherParams.remoteUtilityId);
        query.push("is_history", "1");
        if (filters?.page) query.push("page", filters.page.toString());
        if (filters?.limit) query.push("limit", filters.limit.toString());

        // Apply filters if provided
        if (filters?.searchData) {
          query.push("search_data", filters.searchData);
        }
        if (
          filters?.serviceAssociation &&
          filters.serviceAssociation !== "all"
        ) {
          query.push("association_type", filters.serviceAssociation);
        }
        if (filters?.status && filters.status !== "all") {
          query.push("status", filters.status);
        }
        if (filters?.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }
        if (filters?.area && filters.area !== "all") {
          query.push("area", filters.area);
        }
        if (filters?.technicianId) {
          query.push("technicianId", filters.technicianId);
        }
        if (filters?.slaFilter && filters.slaFilter !== "all") {
          query.push("sla_compliance", filters.slaFilter);
        }
        if (filters?.soNameFilter) {
          query.push("so_name", filters.soNameFilter);
        }
        query.push("export_format", "csv");
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },
  // OPTIONAL: Generic endpoint that can handle any combination of flags
  list: async (params: JobCardListParams): Promise<JobCardApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);

        // Add conditional flags
        if (params.isPending !== undefined) {
          query.push("is_pending", params.isPending ? "1" : "0");
        }
        if (params.isAssigned !== undefined) {
          query.push("is_assigned", params.isAssigned ? "1" : "0");
        }
        if (params.isCompleted !== undefined) {
          query.push("is_completed", params.isCompleted ? "1" : "0");
        }
        if (params.isHistory !== undefined) {
          query.push("is_history", params.isHistory ? "1" : "0");
        }

        query.push("page", (params.page || 1).toString());
        query.push("limit", params.limit?.toString() || PAGE_SIZE);
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },
  detail: async (params: JobCardDetailParams): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `job-card/detail/${params.id}`,
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
      }
    );

    const response = await wxApiClient.get(url);

    return response.data.result;
  },
  consumer: async (
    params: ConsumerSearchParams
  ): Promise<ConsumerSearchResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "search-consumer-list",
      (query) => {
        // Always add remote_utility_id
        query.push("remote_utility_id", params.remoteUtilityId.toString());

        // Add limit if provided, default to 5
        if (params.limit !== undefined) {
          query.push("limit", params.limit.toString());
        } else {
          query.push("limit", "5");
        }

        // Add page if provided, default to 1
        if (params.page !== undefined) {
          query.push("page", params.page.toString());
        } else {
          query.push("page", "1");
        }

        // Add searchData if provided
        if (
          params.searchData !== undefined &&
          params.searchData !== "" &&
          params.searchData !== null
        ) {
          query.push("search_data", params.searchData.toString());
        }
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },
  meter: async (params: MeterSearchParams): Promise<MeterSearchResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "search-meter-list",
      (query) => {
        // Always add remote_utility_id
        query.push("remote_utility_id", params.remoteUtilityId.toString());

        // Add limit if provided, default to 5
        if (params.limit !== undefined) {
          query.push("limit", params.limit.toString());
        } else {
          query.push("limit", "5");
        }

        // Add page if provided, default to 1
        if (params.page !== undefined) {
          query.push("page", params.page.toString());
        } else {
          query.push("page", "1");
        }
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },
  asset: async (params: AssetSearchParams): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "get-data-by-entity",
      (query) => {
        // Always add remote_utility_id
        query.push("sop_id", params.sopId.toString());
        query.push("remote_utility_id", params.remoteUtilityId);
        // Add limit if provided, default to 5
        if (params.limit !== undefined) {
          query.push("limit", params.limit.toString());
        } else {
          query.push("limit", "5");
        }

        // Add page if provided, default to 1
        if (params.page !== undefined) {
          query.push("page", params.page.toString());
        } else {
          query.push("page", "1");
        }
        if (params.searchData)
        {
          query.push("search", params.searchData);
        }
      }
    );

    const response = await wxApiClient.get(url);

    return response.data;
  },
  create: async (
    requestBody: CreateServiceOrderRequest
  ): Promise<CreateServiceOrderResponse> => {

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "multiple-job-card"
    );
    const response = await wxApiClient.post(url, requestBody);

    return response.data;
  },
  recommendation: async (
    params: JobCardRecommendationParams
  ): Promise<JobCardRecommendationResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card-recommendation",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("field_force_id", params.fieldForceId);
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },
  fieldForceRecommend: async (
    params: FieldForceRecommendParams
  ): Promise<FieldForceRecommendResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "field-force-recommend",
      (query) => {
        // Always add these parameters
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("sop_id", params.sopId);

        // Conditionally add meter_locations for Meter association
        if (params.meterLocations && params.meterLocations.length > 0) {
          // params.meterLocations.forEach((location) => {
          //   query.push("meter_locations", location);
          // });
        }

        // Add search parameter if provided
        if (params.searchData && params.searchData.trim()) {
          query.push("search_data", params.searchData);
        }

        // Add pagination parameters
        if (params.page) {
          query.push("page", params.page.toString());
        }
        if (params.limit) {
          query.push("limit", params.limit.toString());
        }
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },
  downloadReport: async (
    reportId: number,
    remoteUtilityId: string,
    tabName?: string
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `download-report/${reportId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (tabName != "") {
          query.push("tab_name", tabName);
        }
      }
    );
    const response = await wxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
  rejectJobCard: async (
    jobCardId: number,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `job-cards/${jobCardId}/reject`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await wxApiClient.put(url);
    return response.data;
  },

  acceptJobCard: async (
    jobCardId: number,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `job-cards/${jobCardId}/accept`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await wxApiClient.put(url);
    return response.data;
  },
};
export const serviceProcedureApi = {
  // Get list of service procedures with pagination and filters
  list: async (
    params: ServiceProcedureListParams
  ): Promise<ServiceProcedureApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "get-service-procedure",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);

        if (params.isActive !== undefined) {
          query.push("is_active", params.isActive ? "true" : "false");
        }

        if (params.sopType) {
          query.push("sop_type", params.sopType);
        }

        if (params.sopSubtype) {
          query.push("sop_subtype", params.sopSubtype);
        }

        if (params.utilityService) {
          query.push("utility_service", params.utilityService);
        }

        if (params.sopAssociationTypeDisplay) {
          query.push(
            "association_type",
            params.sopAssociationTypeDisplay.toUpperCase()
          );
        }

        if (params.searchTerm) {
          query.push("search_data", params.searchTerm);
        }

        query.push("page", (params.page || 1).toString());
        query.push("limit", params.limit?.toString() || PAGE_SIZE);
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },
  getTerritoryConfig: async (
    utilityId: string,
    configLevel: "area" | "sub_area" | "premise",
    codeList?: string
  ): Promise<TerritoryConfigResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("config_level", configLevel);
        if (
          (configLevel === "sub_area" || configLevel === "premise") &&
          codeList
        ) {
          query.push("code_list", codeList);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
  getServiceAssociations: async (
    utilityId: string
  ): Promise<ServiceAssociationResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "choices/service_association",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  getRejectionReason: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "choices/rejection_reason");
    const response = await wxApiClient.get(url);
    return response.data;
  },

  getPriority: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "choices/priority");
    const response = await wxApiClient.get(url);
    return response.data;
  },
getRejectedSO: async (params: {
  remote_utility_id: string;
  is_rejected: string;
  page: any;
  limit: any;
  search_data?: string;
  status?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "job-card",
    (qs) => {
      qs.push("remote_utility_id", params.remote_utility_id);
      qs.push("is_rejected", params.is_rejected);
      qs.push("page", params.page);
      qs.push("limit", params.limit);
      if (params.search_data) {
        qs.push("search_data", params.search_data);
      }
      if (params.status) {
        qs.push("status", params.status);
      }
    }
  );
  const response = await wxApiClient.get(url);
  return response.data;
},

getRejectedSummary: async (params: {
  remote_utility_id: string;

}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "job-card/rejected/summary",
    (qs) => {
      qs.push("remote_utility_id", params.remote_utility_id);
    }
  );
  const response = await wxApiClient.get(url);
  return response.data;
},

  rejectServiceOrder: async (id: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", `job-cards/${id}/reject`);
    const response = await wxApiClient.put(url, payload);
    return response.data;
  },

  reworkOrder: async (id: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", `create-rework-order/${id}`);
    const response = await wxApiClient.post(url, payload);
    return response.data;
  },

  getRejectedSODetail: async (params: {
    remote_utility_id: string;
    id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `job-card/detail/${params.id}`,
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

export const assignJobCards = async (
  assignmentData: AssignmentRequest
): Promise<AssignmentResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "wx",
    "bulk-assign/job-card"
  );

  const response = await wxApiClient.put(url, assignmentData);
  return response.data;
};

  export const assignedJobCards = async (payload: any): Promise<any> => {
    try {
      const response = await wxApiClient.put("assigned-job-card/", payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  };

  export const updateJobCardPriority = async (payload: {
    job_card_id: number;
    priority: number;
    remote_utility_id: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/update-priority"
    );
    const response = await wxApiClient.patch(url, payload);
    return response.data;
  };

  export const getSoCounts = async (
    startDate?: string,
    endDate?: string,
    utilityId?: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "job-card/current/summary",
      (query) => {
        if (utilityId) query.push("remote_utility_id", utilityId);
        if (startDate) query.push("start_date", startDate);
        if (endDate) query.push("end_date", endDate);
      }
    );
    const response = await wxApiClient.get(url);
    return response.data?.result ?? response.data;
  };

  export const generateReport = async (payload: {
    remote_utility_id: number | string;
    groups: string[];
    start_date?: string;
    end_date?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "dispatcher-generate-report",
      (query) => {
        query.push("export", "true");
      }
    );
    const response = await wxApiClient.post(url, payload);
    return response.data;
  };

  export const getGeneratedReport = async (params: {
    remote_utility_id: number | string;
    start_date?: string;
    end_date?: string;
    groups?: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "dispatcher-generate-report",
      (query) => {
        query.push("export", "true");
        query.push("remote_utility_id", params.remote_utility_id.toString());
        if (params.start_date) query.push("start_date", params.start_date);
        if (params.end_date) query.push("end_date", params.end_date);
        if (Array.isArray(params.groups)) {
          params.groups.forEach((group) => query.push("groups", group));
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  };